import { useLayoutEffect, useState } from 'react';

const MAX_ITERATIONS = 20;
const STABLE_FRAMES_REQUIRED = 3;

/**
 * Returns true when the container's height has been stable for two consecutive
 * animation frames. Use this to know when it is safe to run layout-dependent
 * calculations (e.g. page delimitation) that should not run while the container
 * is still resizing (fonts, reflows, etc.).
 *
 * @param containerRef - Ref to the main container element (its height is watched).
 * @param deps - When these change, stability is reset and the check runs again.
 * @returns isStable - True when the container height has settled.
 */
export function useContainerStable(
  containerRef: React.RefObject<HTMLElement | null>,
  deps: React.DependencyList,
): boolean {
  const [isStable, setIsStable] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setIsStable(false);
      return;
    }

    setIsStable(false);
    let rafId: number;
    let lastSignature: string | null = null;
    let stableFrames = 0;
    let iterations = 0;

    const run = (): void => {
      const rect = container.getBoundingClientRect();
      const signature = [
        rect.width.toFixed(1),
        rect.height.toFixed(1),
        container.scrollWidth,
        container.scrollHeight,
      ].join(':');

      if (signature === lastSignature) {
        stableFrames += 1;
      } else {
        stableFrames = 1;
        lastSignature = signature;
      }

      if (stableFrames >= STABLE_FRAMES_REQUIRED || iterations >= MAX_ITERATIONS) {
        setIsStable(true);
        return;
      }

      iterations += 1;
      rafId = requestAnimationFrame(run);
    };

    rafId = requestAnimationFrame(run);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, ...deps]);

  return isStable;
}
